// AdminChatContext: ONE assistant conversation for the whole admin app.
//
// The Home tab and the docked panel in Workflows are two VIEWS of the same
// conversation, not two chat systems. Everything conversational lives here:
// the turns, the server thread id (admin_chat_threads), the in-flight state and
// the follow-up chips. A surface that wants to show the chat reads this; a
// surface that wants to continue it calls send().
//
// That is what makes "expand into Home" free: there is nothing to hand over,
// because both surfaces were already rendering the same state.
//
// Per-surface UI state (composer text, caret, mention menu, history list) stays
// local to each view. Only the conversation itself is shared.

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { CopilotGap, CopilotPlan, CopilotVerdict } from "@/lib/workflows/copilot";

export const ASSISTANT_UNREACHABLE =
  "I couldn't reach the Dibs AI service just now. Please try again in a moment.";

export interface AdminChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  /** Workflow-building turns carry a plan; ordinary chat turns don't. */
  verdict?: CopilotVerdict;
  gaps?: CopilotGap[];
  plan?: CopilotPlan;
  /** A one-click follow-up offered when nothing could be built. */
  suggestion?: string;
  /** Set once the owner builds the plan. The card locks to its done state. */
  committedWorkflowId?: string;
}

interface AdminChatValue {
  messages: AdminChatMessage[];
  threadId: string | null;
  isThinking: boolean;
  followUps: string[];
  hasConversation: boolean;
  send: (text: string, opts?: { workflowContext?: boolean }) => Promise<void>;
  openThread: (threadId: string) => Promise<void>;
  newChat: () => void;
  /** Marks the plan on a turn as built. */
  markPlanBuilt: (messageId: string, workflowId: string) => void;
}

/** One assistant turn. Swappable so the dev preview can run on fixtures. */
export type AdminChatAsk = (input: {
  message: string;
  threadId: string | null;
  /** True when the turn came from the Workflows panel. Settles build intent. */
  workflowContext?: boolean;
}) => Promise<{
  threadId?: string;
  /**
   * The persisted assistant turn's id. Needed to act on a turn later,
   * build_workflow addresses a plan by the message that proposed it, so the
   * client must adopt it rather than minting its own.
   */
  messageId?: string;
  reply: string;
  suggestions?: string[];
  verdict?: CopilotVerdict;
  gaps?: CopilotGap[];
  plan?: CopilotPlan;
  suggestion?: string;
}>;

/** The live turn: one call to the admin-assistant edge function. */
const askAdminAssistant: AdminChatAsk = async ({ message, threadId, workflowContext }) => {
  const { data, error } = await supabase.functions.invoke("admin-assistant", {
    body: {
      action: "ask",
      message,
      thread_id: threadId ?? undefined,
      ...(workflowContext ? { workflow_context: true } : {}),
    },
  });
  if (error) throw error;
  return {
    threadId: data?.thread_id as string | undefined,
    messageId: data?.message_id as string | undefined,
    reply: (data?.reply as string) ?? "",
    suggestions: data?.ok && Array.isArray(data.suggestions)
      ? (data.suggestions as string[])
      : [],
    // Present only on workflow-drafting turns; absent on ordinary answers.
    verdict: data?.verdict as CopilotVerdict | undefined,
    gaps: data?.gaps as CopilotGap[] | undefined,
    plan: data?.plan as CopilotPlan | undefined,
    suggestion: data?.suggestion as string | undefined,
  };
};

const AdminChatContext = createContext<AdminChatValue | null>(null);

export function AdminChatProvider({
  children,
  ask,
}: {
  children: ReactNode;
  /** Overrides the live admin-assistant call. Dev/preview use only. */
  ask?: AdminChatAsk;
}) {
  const { businessId } = useAuth();
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<AdminChatMessage[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [isThinking, setIsThinking] = useState(false);
  const [followUps, setFollowUps] = useState<string[]>([]);

  const send = useCallback(
    async (raw: string, opts?: { workflowContext?: boolean }) => {
      const text = raw.trim();
      if (!text || isThinking) return;

      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: "user", content: text },
      ]);
      setFollowUps([]);
      setIsThinking(true);

      // The admin-assistant edge fn grounds the answer in this business's
      // bookings/contacts/conversations, persists the thread, and returns
      // proactive follow-up suggestions alongside the reply.
      try {
        const turn = ask
          ? await ask({ message: text, threadId, workflowContext: opts?.workflowContext })
          : await askAdminAssistant({ message: text, threadId, workflowContext: opts?.workflowContext });

        if (turn.threadId) {
          setThreadId(turn.threadId);
          // Surface the new/bumped thread in Home's "Conversation history".
          queryClient.invalidateQueries({ queryKey: ["chat-history", businessId] });
        }

        setMessages((prev) => [
          ...prev,
          {
            // Server id where we have one: a locally-minted uuid would name a
            // turn the backend has never heard of, and Build addresses turns
            // by id. Falls back only when the turn was never persisted.
            id: turn.messageId ?? crypto.randomUUID(),
            role: "assistant",
            content: turn.reply || ASSISTANT_UNREACHABLE,
            // Present only once the assistant can draft workflows server-side.
            verdict: turn.verdict,
            gaps: turn.gaps,
            plan: turn.plan,
            suggestion: turn.suggestion,
          },
        ]);

        setFollowUps((turn.suggestions ?? []).filter((s) => !!s?.trim()));
      } catch {
        setMessages((prev) => [
          ...prev,
          { id: crypto.randomUUID(), role: "assistant", content: ASSISTANT_UNREACHABLE },
        ]);
      } finally {
        setIsThinking(false);
      }
    },
    [ask, businessId, isThinking, queryClient, threadId],
  );

  // Re-open a past conversation: load its turns oldest-first and adopt its
  // thread id, so follow-ups continue the same conversation server-side.
  const openThread = useCallback(async (id: string) => {
    setFollowUps([]);
    setThreadId(id);
    setMessages([{ id: `${id}-loading`, role: "assistant", content: "…" }]);
    // Plans live on the turn that proposed them, so reopening a conversation
    // brings its plan cards back with it, including whether they were built.
    // The staged graph is deliberately NOT readable here; the card only needs
    // the narration.
    // `as any`: the generated Supabase types predate migration
    // 20260828120000, which adds the workflow_* columns. Regenerate types once
    // it has been applied and this cast can go. Same idiom Automations.tsx uses.
    const { data } = await (supabase as any)
      .from("admin_chat_messages")
      .select("id, role, content, created_at, workflow_plan, workflow_verdict, workflow_gaps, committed_workflow_id")
      .eq("thread_id", id)
      .order("created_at", { ascending: true });
    setMessages(
      ((data ?? []) as Array<{
        id: string;
        role: "user" | "assistant";
        content: string;
        workflow_plan?: CopilotPlan | null;
        workflow_verdict?: CopilotVerdict | null;
        workflow_gaps?: CopilotGap[] | null;
        committed_workflow_id?: string | null;
      }>).map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        ...(m.workflow_plan ? { plan: m.workflow_plan } : {}),
        ...(m.workflow_verdict ? { verdict: m.workflow_verdict } : {}),
        ...(m.workflow_gaps?.length ? { gaps: m.workflow_gaps } : {}),
        ...(m.committed_workflow_id ? { committedWorkflowId: m.committed_workflow_id } : {}),
      })),
    );
  }, []);

  const newChat = useCallback(() => {
    setMessages([]);
    setIsThinking(false);
    setThreadId(null);
    setFollowUps([]);
  }, []);

  const markPlanBuilt = useCallback((messageId: string, workflowId: string) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, committedWorkflowId: workflowId } : m)),
    );
  }, []);

  const value = useMemo<AdminChatValue>(
    () => ({
      messages,
      threadId,
      isThinking,
      followUps,
      hasConversation: messages.length > 0,
      send,
      openThread,
      newChat,
      markPlanBuilt,
    }),
    [messages, threadId, isThinking, followUps, send, openThread, newChat, markPlanBuilt],
  );

  return <AdminChatContext.Provider value={value}>{children}</AdminChatContext.Provider>;
}

export function useAdminChat(): AdminChatValue {
  const ctx = useContext(AdminChatContext);
  if (!ctx) throw new Error("useAdminChat must be used inside an AdminChatProvider");
  return ctx;
}
